import {
  Card,
  message,
  Table,
  Form,
  Row,
  Col,
  Input,
  Modal,
  Button,
  Tooltip,
} from "antd"
import { MdEdit } from "react-icons/md"
import * as API from "common/api"
import * as COMMONS from "common/common"
import moment from "moment"
import "moment/locale/ja"
import React, { useEffect, useRef, useState } from "react"
import { useQuery, useMutation, useQueryClient } from "react-query"
import { Helmet } from "react-helmet-async"
import { useNavigate } from "react-router-dom"
import styled from "styled-components"
import useResizeObserver from "@react-hook/resize-observer"

moment.updateLocale("ja")

const CustomTable = styled(Table)`
  .ant-table-ping-right,
  .ant-table-ping-left tr th.ant-table-cell {
    cursor: grab;
    cursor: -webkit-grab;
  }

  tr.active th.ant-table-cell {
    cursor: grabbing;
    cursor: -webkit-grabbing;
  }
`

const useSize = (target) => {
  const [size, setSize] = React.useState()

  React.useLayoutEffect(() => {
    setSize(target.current.getBoundingClientRect())
  }, [target])

  useResizeObserver(target, (entry) => setSize(entry.contentRect))
  return size
}

const { TextArea } = Input

const templateTypeChecker = (type, typeArray) => {
  return typeArray.includes(type)
}

const Notification = (props) => {
  const navigate = useNavigate()

  const isMountedRef = COMMONS.USE_IS_MOUNTED_REF()
  const [replyTemplateForm] = Form.useForm()
  const queryClient = useQueryClient()

  const tableRef = useRef(null)
  const size = useSize(tableRef)
  const [isScrollableHintVisible, setIsScrollableHintVisible] = useState(false)

  const [isReplyTemplateModalVisible, setIsReplyTemplateModalVisible] =
    useState(false)

  const [replyTemplates, setReplyTemplates] = useState([])

  const [currentReplyTemplate, setCurrentReplyTemplate] = useState(undefined)

  useQuery(
    [API.QUERY_KEY_ADMIN_REPLY_TEMPLATES, {}],
    () => API.ADMIN_GET_REPLY_TEMPLATES(),
    {
      onSuccess: (response) => {
        if (isMountedRef.current) {
          setReplyTemplates(response?.data || [])
        }
      },
      onError: (error) => {
        if (error?.response?.status === COMMONS.RESPONSE_PERMISSION_ERROR) {
          navigate(COMMONS.PERMISSION_ERROR_ROUTE)
        } else if (error?.response?.status === COMMONS.RESPONSE_SESSION_ERROR) {
          message.warning(COMMONS.ERROR_SESSION_MSG)
          navigate(COMMONS.ADMIN_LOGIN_ROUTE)
        } else if (error?.response?.status === COMMONS.RESPONSE_SYSTEM_ERROR) {
          message.error(COMMONS.ERROR_SYSTEM_MSG)
        } else {
          message.error(COMMONS.ERROR_SYSTEM_MSG)
        }
      },
    }
  )

  const replyTemplateUpdateMutation = useMutation(
    API.ADMIN_UPDATE_REPLY_TEMPLATE,
    {
      onSuccess: () => {
        message.success(COMMONS.SUCCESS_SAVE_MSG)
        hideReplyTemplateModal()
        queryClient.invalidateQueries(API.QUERY_KEY_ADMIN_REPLY_TEMPLATES)
      },
      onError: (error) => {
        if (error?.response?.status === COMMONS.RESPONSE_PERMISSION_ERROR) {
          navigate(COMMONS.PERMISSION_ERROR_ROUTE)
        } else if (error?.response?.status === COMMONS.RESPONSE_SESSION_ERROR) {
          message.warning(COMMONS.ERROR_SESSION_MSG)
          navigate(COMMONS.ADMIN_LOGIN_ROUTE)
        } else if (error?.response?.status === COMMONS.RESPONSE_SYSTEM_ERROR) {
          message.error(COMMONS.ERROR_SYSTEM_MSG)
        } else {
          message.error(COMMONS.ERROR_SYSTEM_MSG)
        }
      },
    }
  )

  useEffect(() => {
    const slider = document.querySelector("thead.ant-table-thead tr")
    const body = document.querySelector(".ant-table-container .ant-table-body")
    let isDown = false
    let startX
    let scrollLeft

    slider.addEventListener("mousedown", (e) => {
      isDown = true
      slider.classList.add("active")
      startX = e.pageX - body.offsetLeft
      scrollLeft = body.scrollLeft
    })

    slider.addEventListener("mouseleave", () => {
      isDown = false
      slider.classList.remove("active")
    })

    slider.addEventListener("mouseup", () => {
      isDown = false
      slider.classList.remove("active")
    })

    slider.addEventListener("mousemove", (e) => {
      if (!isDown) {
        return
      }

      e.preventDefault()
      const x = e.pageX - body.offsetLeft
      const walk = (x - startX) * 3 //scroll-fast
      body.scrollLeft = scrollLeft - walk
    })
  }, [])

  useEffect(() => {
    if (
      tableRef?.current?.querySelector(".ant-table-ping-right") ||
      tableRef?.current?.querySelector(".ant-table-ping-left")
    ) {
      setIsScrollableHintVisible(true)
    } else {
      setIsScrollableHintVisible(false)
    }
  }, [size])

  const handleReplyTemplate = (data) => {
    const paramData = {
      type: currentReplyTemplate.type,
      title: data.title,
      body: data.content,
    }

    replyTemplateUpdateMutation.mutate(paramData)
  }

  const showReplyTemplateModal = (replyTemplate) => {
    setCurrentReplyTemplate(replyTemplate)

    replyTemplateForm.setFieldsValue({
      title: replyTemplate?.title,
      content: replyTemplate?.body,
    })

    setIsReplyTemplateModalVisible(true)
  }

  const hideReplyTemplateModal = () => {
    setCurrentReplyTemplate(undefined)
    setIsReplyTemplateModalVisible(false)
  }

  const replyTemplateColumns = [
    {
      key: "type",
      title: "タイプ",
      dataIndex: "type",
      align: "center",
      width: 150,
      render: (type) => (
        <p className="whitespace-pre-wrap">
          {COMMONS.REPLY_TEMPLATES.find((rt) => rt.value === type)?.text ||
            "ー"}
        </p>
      ),
    },
    {
      key: "title",
      title: "タイトル",
      dataIndex: "title",
      align: "center",
      width: 150,
    },
    {
      key: "body",
      title: "内容",
      dataIndex: "body",
      align: "center",
      width: 400,
      render: (type) => <p className="whitespace-pre-wrap">{type}</p>,
    },
    {
      key: "action",
      title: "",
      dataIndex: "replyTemplate",
      align: "center",
      width: 50,
      render: (replyTemplate) => (
        <>
          <Tooltip title="変更" placement="left">
            <Button
              className="m-1"
              icon={<MdEdit className="inline-block anticon" />}
              onClick={() => {
                showReplyTemplateModal(replyTemplate)
              }}
            />
          </Tooltip>
        </>
      ),
    },
  ]

  return (
    <>
      <Helmet>
        <title>自動通知設定 | {process.env.REACT_APP_SYSTEM_NAME}</title>
      </Helmet>
      <Card
        title="自動通知設定"
        className="animate__animated animate__fadeIn"
        bordered={false}
      >
        <Tooltip
          visible={isScrollableHintVisible}
          placement="topRight"
          title="⇦スクロールできます⇨"
          overlayStyle={{
            zIndex: 999,
          }}
        >
          <CustomTable
            ref={tableRef}
            bordered
            size="small"
            columns={replyTemplateColumns}
            scroll={{
              x: 640,
              y: 720,
            }}
            locale={{ emptyText: "自動通知がありません" }}
            pagination={{
              responsive: true,
              defaultPageSize: 20,
              showTotal: (total, range) =>
                `全${total}件中${range[0]}～${range[1]}件目`,
              position: ["bottomRight"],
            }}
            dataSource={
              replyTemplates
                ? replyTemplates.map((replyTemplate) => {
                    return {
                      key: replyTemplate.type,
                      type: replyTemplate.type || "ー",
                      title: replyTemplate.title || "ー",
                      body: replyTemplate.body || "ー",
                      replyTemplate: replyTemplate,
                    }
                  })
                : []
            }
          />
        </Tooltip>
      </Card>
      <Modal
        title={`${
          COMMONS.REPLY_TEMPLATES.find(
            (rt) => rt.value === currentReplyTemplate?.type
          )?.title || "ー"
        }`}
        visible={isReplyTemplateModalVisible}
        onCancel={hideReplyTemplateModal}
        bodyStyle={{
          maxHeight: "90vh",
          overflowY: "auto",
          overflowX: "hidden",
        }}
        footer={null}
        centered
        destroyOnClose
      >
        <Row>
          <Col span={24}>
            <Form
              name="replyTemplateForm"
              form={replyTemplateForm}
              onFinish={handleReplyTemplate}
              layout="vertical"
              preserve={false}
              requiredMark={false}
              size="large"
              initialValues={{
                title: "",
                content: "",
              }}
              scrollToFirstError
            >
              <Row gutter={[0, 16]}>
                <Col span={24}>
                  <Form.Item
                    name="title"
                    label="タイトル"
                    rules={[
                      {
                        required: true,
                        message: "タイトルを入力してください",
                      },
                    ]}
                  >
                    <Input
                      allowClear
                      placeholder="タイトルを入力してください"
                    />
                  </Form.Item>
                </Col>
                <Col span={24}>
                  <Form.Item
                    name="content"
                    label="内容"
                    className="whitespace-pre-wrap mb-4"
                    rules={[
                      {
                        required: true,
                        message: "内容を入力してください",
                      },
                    ]}
                    extra={`\nメッセージ内容のキーワードがそれぞれの値に変わります：\n※[NAME]　➝　お客様の氏名\n※[LINK]　➝　ECサイトのリンク${
                      templateTypeChecker(currentReplyTemplate?.type, [
                        COMMONS.REPLY_TEMPLATE_FORGOT_PASSWORD_VALUE,
                      ])
                        ? "\n※[URL]　➝　確認リンク"
                        : ""
                    }${
                      templateTypeChecker(currentReplyTemplate?.type, [
                        COMMONS.REPLY_TEMPLATE_LAYAWAY_COMPLETE_VALUE,
                        COMMONS.REPLY_TEMPLATE_PURCHASE_COMPLETE_VALUE,
                        COMMONS.REPLY_TEMPLATE_SHIPMENT_COMPLETE_VALUE,
                        COMMONS.REPLY_TEMPLATE_LAYAWAY_REMIND_VALUE,
                        COMMONS.REPLY_TEMPLATE_LAYAWAY_REMIND_BEFORE_11_VALUE,
                        COMMONS.REPLY_TEMPLATE_LAYAWAY_REMIND_BEFORE_17_VALUE,
                        COMMONS.REPLY_TEMPLATE_LAYAWAY_REMIND_BEFORE_18_VALUE,
                        COMMONS.REPLY_TEMPLATE_LAYAWAY_REMIND_AFTER_11_VALUE,
                        COMMONS.REPLY_TEMPLATE_LAYAWAY_REMIND_AFTER_17_VALUE,
                        COMMONS.REPLY_TEMPLATE_LAYAWAY_REMIND_AFTER_18_VALUE,
                      ])
                        ? "\n※[ORDER_ID]　➝　注文番号"
                        : ""
                    }${
                      templateTypeChecker(currentReplyTemplate?.type, [
                        COMMONS.REPLY_TEMPLATE_LAYAWAY_COMPLETE_VALUE,
                        COMMONS.REPLY_TEMPLATE_PURCHASE_COMPLETE_VALUE,
                        COMMONS.REPLY_TEMPLATE_SHIPMENT_COMPLETE_VALUE,
                        COMMONS.REPLY_TEMPLATE_LAYAWAY_REMIND_VALUE,
                        COMMONS.REPLY_TEMPLATE_LAYAWAY_REMIND_BEFORE_11_VALUE,
                        COMMONS.REPLY_TEMPLATE_LAYAWAY_REMIND_BEFORE_17_VALUE,
                        COMMONS.REPLY_TEMPLATE_LAYAWAY_REMIND_BEFORE_18_VALUE,
                        COMMONS.REPLY_TEMPLATE_LAYAWAY_REMIND_AFTER_11_VALUE,
                        COMMONS.REPLY_TEMPLATE_LAYAWAY_REMIND_AFTER_17_VALUE,
                        COMMONS.REPLY_TEMPLATE_LAYAWAY_REMIND_AFTER_18_VALUE,
                      ])
                        ? "\n※[ORDER_DT]　➝　注文日付"
                        : ""
                    }${
                      templateTypeChecker(currentReplyTemplate?.type, [
                        COMMONS.REPLY_TEMPLATE_PURCHASE_COMPLETE_VALUE,
                        COMMONS.REPLY_TEMPLATE_SHIPMENT_COMPLETE_VALUE,
                      ])
                        ? "\n※[ADDRESS]　➝　お届け先"
                        : ""
                    }${
                      templateTypeChecker(currentReplyTemplate?.type, [
                        COMMONS.REPLY_TEMPLATE_LAYAWAY_COMPLETE_VALUE,
                        COMMONS.REPLY_TEMPLATE_PURCHASE_COMPLETE_VALUE,
                        COMMONS.REPLY_TEMPLATE_SHIPMENT_COMPLETE_VALUE,
                        COMMONS.REPLY_TEMPLATE_LAYAWAY_REMIND_VALUE,
                        COMMONS.REPLY_TEMPLATE_LAYAWAY_REMIND_BEFORE_11_VALUE,
                        COMMONS.REPLY_TEMPLATE_LAYAWAY_REMIND_BEFORE_17_VALUE,
                        COMMONS.REPLY_TEMPLATE_LAYAWAY_REMIND_BEFORE_18_VALUE,
                        COMMONS.REPLY_TEMPLATE_LAYAWAY_REMIND_AFTER_11_VALUE,
                        COMMONS.REPLY_TEMPLATE_LAYAWAY_REMIND_AFTER_17_VALUE,
                        COMMONS.REPLY_TEMPLATE_LAYAWAY_REMIND_AFTER_18_VALUE,
                      ])
                        ? "\n※[COMMENT]　➝　コメント"
                        : ""
                    }${
                      templateTypeChecker(currentReplyTemplate?.type, [
                        COMMONS.REPLY_TEMPLATE_LAYAWAY_COMPLETE_VALUE,
                        COMMONS.REPLY_TEMPLATE_PURCHASE_COMPLETE_VALUE,
                        COMMONS.REPLY_TEMPLATE_SHIPMENT_COMPLETE_VALUE,
                        COMMONS.REPLY_TEMPLATE_LAYAWAY_REMIND_VALUE,
                        COMMONS.REPLY_TEMPLATE_LAYAWAY_REMIND_BEFORE_11_VALUE,
                        COMMONS.REPLY_TEMPLATE_LAYAWAY_REMIND_BEFORE_17_VALUE,
                        COMMONS.REPLY_TEMPLATE_LAYAWAY_REMIND_BEFORE_18_VALUE,
                        COMMONS.REPLY_TEMPLATE_LAYAWAY_REMIND_AFTER_11_VALUE,
                        COMMONS.REPLY_TEMPLATE_LAYAWAY_REMIND_AFTER_17_VALUE,
                        COMMONS.REPLY_TEMPLATE_LAYAWAY_REMIND_AFTER_18_VALUE,
                      ])
                        ? "\n※[ITEMS]　➝　注文の商品"
                        : ""
                    }${
                      templateTypeChecker(currentReplyTemplate?.type, [
                        COMMONS.REPLY_TEMPLATE_LAYAWAY_COMPLETE_VALUE,
                        COMMONS.REPLY_TEMPLATE_PURCHASE_COMPLETE_VALUE,
                        COMMONS.REPLY_TEMPLATE_SHIPMENT_COMPLETE_VALUE,
                        COMMONS.REPLY_TEMPLATE_LAYAWAY_REMIND_VALUE,
                        COMMONS.REPLY_TEMPLATE_LAYAWAY_REMIND_BEFORE_11_VALUE,
                        COMMONS.REPLY_TEMPLATE_LAYAWAY_REMIND_BEFORE_17_VALUE,
                        COMMONS.REPLY_TEMPLATE_LAYAWAY_REMIND_BEFORE_18_VALUE,
                        COMMONS.REPLY_TEMPLATE_LAYAWAY_REMIND_AFTER_11_VALUE,
                        COMMONS.REPLY_TEMPLATE_LAYAWAY_REMIND_AFTER_17_VALUE,
                        COMMONS.REPLY_TEMPLATE_LAYAWAY_REMIND_AFTER_18_VALUE,
                      ])
                        ? "\n※[ITEMS_TOTAL]　➝　商品金額"
                        : ""
                    }${
                      templateTypeChecker(currentReplyTemplate?.type, [
                        COMMONS.REPLY_TEMPLATE_PURCHASE_COMPLETE_VALUE,
                        COMMONS.REPLY_TEMPLATE_SHIPMENT_COMPLETE_VALUE,
                      ])
                        ? "\n※[SHIPMENT_TOTAL]　➝　送料"
                        : ""
                    }${
                      templateTypeChecker(currentReplyTemplate?.type, [
                        COMMONS.REPLY_TEMPLATE_LAYAWAY_COMPLETE_VALUE,
                        COMMONS.REPLY_TEMPLATE_PURCHASE_COMPLETE_VALUE,
                        COMMONS.REPLY_TEMPLATE_SHIPMENT_COMPLETE_VALUE,
                        COMMONS.REPLY_TEMPLATE_LAYAWAY_REMIND_VALUE,
                        COMMONS.REPLY_TEMPLATE_LAYAWAY_REMIND_BEFORE_11_VALUE,
                        COMMONS.REPLY_TEMPLATE_LAYAWAY_REMIND_BEFORE_17_VALUE,
                        COMMONS.REPLY_TEMPLATE_LAYAWAY_REMIND_BEFORE_18_VALUE,
                        COMMONS.REPLY_TEMPLATE_LAYAWAY_REMIND_AFTER_11_VALUE,
                        COMMONS.REPLY_TEMPLATE_LAYAWAY_REMIND_AFTER_17_VALUE,
                        COMMONS.REPLY_TEMPLATE_LAYAWAY_REMIND_AFTER_18_VALUE,
                      ])
                        ? "\n※[ORDER_TOTAL]　➝　合計"
                        : ""
                    }${
                      templateTypeChecker(currentReplyTemplate?.type, [
                        COMMONS.REPLY_TEMPLATE_LAYAWAY_COMPLETE_VALUE,
                        COMMONS.REPLY_TEMPLATE_LAYAWAY_REMIND_VALUE,
                        COMMONS.REPLY_TEMPLATE_LAYAWAY_REMIND_BEFORE_11_VALUE,
                        COMMONS.REPLY_TEMPLATE_LAYAWAY_REMIND_BEFORE_17_VALUE,
                        COMMONS.REPLY_TEMPLATE_LAYAWAY_REMIND_BEFORE_18_VALUE,
                        COMMONS.REPLY_TEMPLATE_LAYAWAY_REMIND_AFTER_11_VALUE,
                        COMMONS.REPLY_TEMPLATE_LAYAWAY_REMIND_AFTER_17_VALUE,
                        COMMONS.REPLY_TEMPLATE_LAYAWAY_REMIND_AFTER_18_VALUE,
                      ])
                        ? "\n※[PICKUP_DT]　➝　受け取る日付と時間"
                        : ""
                    }${
                      templateTypeChecker(currentReplyTemplate?.type, [
                        COMMONS.REPLY_TEMPLATE_LAYAWAY_COMPLETE_VALUE,
                        COMMONS.REPLY_TEMPLATE_LAYAWAY_REMIND_VALUE,
                        COMMONS.REPLY_TEMPLATE_LAYAWAY_REMIND_BEFORE_11_VALUE,
                        COMMONS.REPLY_TEMPLATE_LAYAWAY_REMIND_BEFORE_17_VALUE,
                        COMMONS.REPLY_TEMPLATE_LAYAWAY_REMIND_BEFORE_18_VALUE,
                        COMMONS.REPLY_TEMPLATE_LAYAWAY_REMIND_AFTER_11_VALUE,
                        COMMONS.REPLY_TEMPLATE_LAYAWAY_REMIND_AFTER_17_VALUE,
                        COMMONS.REPLY_TEMPLATE_LAYAWAY_REMIND_AFTER_18_VALUE,
                      ])
                        ? "\n※[BARCODE]　➝　受け取るバーコード"
                        : ""
                    }${
                      templateTypeChecker(currentReplyTemplate?.type, [
                        COMMONS.REPLY_TEMPLATE_SHIPMENT_COMPLETE_VALUE,
                      ])
                        ? "\n※[SHIPMENT_TRACKING]　➝　追跡URL"
                        : ""
                    }`}
                  >
                    <TextArea
                      allowClear
                      bordered
                      showCount
                      autoSize={{ minRows: 10, maxRows: 15 }}
                      maxLength={5000}
                      placeholder="内容を入力してください"
                    />
                  </Form.Item>
                </Col>
                <Col span={24}>
                  <Form.Item
                    className="text-center"
                    style={{ marginBottom: 0 }}
                  >
                    <Button
                      type="primary"
                      htmlType="submit"
                      loading={replyTemplateUpdateMutation.isLoading}
                    >
                      保存
                    </Button>
                  </Form.Item>
                </Col>
              </Row>
            </Form>
          </Col>
        </Row>
      </Modal>
    </>
  )
}

export default Notification
